// src/ConsumerApp/Screens/Search/SearchScreen.js

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../../DesignSystem/Navigation/Navbar';
import CenteredContainer from '../../DesignSystem/Layout/CenteredContainer';
import { theme } from '../../../assets/theme/theme';

// Real SVGs from assets/icons
import Clock from '../../../assets/icons/Clock.svg';
import ToteIcon from '../../../assets/icons/tote.svg';
import ArrowRight from '../../../assets/icons/ArrowRight.svg';
import ShootingStar from '../../../assets/icons/ShootingStar.svg';
import Lightning from '../../../assets/icons/Lightning.svg';

// Custom Hooks
import { useGlobalSearch } from '../../api/search/search.hooks';
import { useGetMostSearchedProducts } from '../../api/products/product.hooks';

const RECENT_SEARCHES_KEY = '@brickbucket_recent_searches';
const RECENTLY_OPENED_KEY = '@brickbucket_recently_opened_items';
const IMAGE_URL =
  process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES || 'https://assets.brickbucket.in/';

// Curated Trending Procurement Searches
const TRENDING_SEARCHES = [
  'Ultratech Cement',
  'Tile Adhesive',
  'CPVC Pipes 1 inch',
  'Waterproofing Compound',
  'TMT Steel Bars',
  'Wall Putty',
  'Switch & Sockets',
];

// Popular Categories Shortcuts
const QUICK_CATEGORIES = [
  { name: 'Cement & Concrete', query: 'Cement' },
  { name: 'Tiles & Flooring', query: 'Tiles' },
  { name: 'Plumbing & Pipes', query: 'Pipes' },
  { name: 'Electrical & Lighting', query: 'Electrical' },
  { name: 'Steel & Metals', query: 'Steel' },
];

export default function SearchScreen() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlQuery = searchParams.get('q') || '';

  const [query, setQuery] = useState(urlQuery);
  const [debouncedQuery, setDebouncedQuery] = useState(urlQuery);
  const [recentSearches, setRecentSearches] = useState([]);
  const [recentlyOpened, setRecentlyOpened] = useState([]);
  const [brokenImages, setBrokenImages] = useState({});

  // 1. Debounce Query Sync
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query.trim());
      if (query.trim().length >= 3) {
        setSearchParams({ q: query.trim() }, { replace: true });
        saveRecentSearch(query.trim());
      } else {
        setSearchParams({}, { replace: true });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  // Sync if URL query param changes
  useEffect(() => {
    if (urlQuery !== query) {
      setQuery(urlQuery);
      setDebouncedQuery(urlQuery);
    }
  }, [urlQuery]);

  // 2. Fetch Live Search Results
  const {
    data: results = [],
    isLoading,
    isFetching,
  } = useGlobalSearch(debouncedQuery, { page: 1, pageSize: 24 });

  // 3. Fetch Popular / Most Searched Products for Empty/Idle State
  const { data: popularProductsData = [], isLoading: isLoadingPopular } = useGetMostSearchedProducts();

  const popularProducts = useMemo(() => {
    const list = Array.isArray(popularProductsData)
      ? popularProductsData
      : popularProductsData?.products || popularProductsData?.data || [];
    return Array.isArray(list) ? list.slice(0, 8) : [];
  }, [popularProductsData]);

  // 4. Local Storage Helpers
  useEffect(() => {
    try {
      const storedSearches = localStorage.getItem(RECENT_SEARCHES_KEY);
      const storedItems = localStorage.getItem(RECENTLY_OPENED_KEY);
      if (storedSearches) setRecentSearches(JSON.parse(storedSearches).slice(0, 6));
      if (storedItems) setRecentlyOpened(JSON.parse(storedItems).slice(0, 8));
    } catch (e) {
      console.error('Error loading search history from localStorage:', e);
    }
  }, []);

  const saveRecentSearch = (term) => {
    const trimmed = term.trim();
    if (!trimmed || trimmed.length < 3) return;

    try {
      const storedSearches = localStorage.getItem(RECENT_SEARCHES_KEY);
      const existing = storedSearches ? JSON.parse(storedSearches) : [];
      const filtered = existing.filter(
        (item) => item.toLowerCase() !== trimmed.toLowerCase()
      );
      const updated = [trimmed, ...filtered].slice(0, 6);
      setRecentSearches(updated);
      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
    } catch (e) {
      console.error('Error saving recent search:', e);
    }
  };

  const saveRecentlyOpenedItem = (item) => {
    try {
      const itemId = item.id || item._id;
      const storedItems = localStorage.getItem(RECENTLY_OPENED_KEY);
      const existing = storedItems ? JSON.parse(storedItems) : [];
      const filtered = existing.filter(
        (opened) => (opened.id || opened._id) !== itemId
      );
      const updated = [item, ...filtered].slice(0, 8);
      setRecentlyOpened(updated);
      localStorage.setItem(RECENTLY_OPENED_KEY, JSON.stringify(updated));
    } catch (e) {
      console.error('Error saving recently opened item:', e);
    }
  };

  const clearHistory = () => {
    setRecentSearches([]);
    setRecentlyOpened([]);
    localStorage.removeItem(RECENT_SEARCHES_KEY);
    localStorage.removeItem(RECENTLY_OPENED_KEY);
  };

  const handleItemPress = (item) => {
    saveRecentlyOpenedItem(item);
    const entityType = (item.type || '').toLowerCase();

    if (entityType === 'category') {
      navigate('/consumer/productslist', {
        state: {
          categoryId: item.id || item._id,
          categoryName: item.name,
        },
      });
    } else if (entityType === 'subcategory') {
      navigate('/consumer/productslist', {
        state: {
          subCategoryId: item.id || item._id,
          subCategoryName: item.name,
          categoryId: item.product_category_id,
          categoryName: item.product_category_name || item.product_catgory_name,
        },
      });
    } else {
      navigate('/consumer/productdetails', {
        state: { productId: item.id || item._id },
      });
    }
  };

  const isSearching = debouncedQuery.length >= 3;

  return (
    <div style={styles.pageShell}>
      <Navbar searchQuery={query} onSearchChange={setQuery} />

      <main style={styles.mainContainer}>
        <CenteredContainer>
          <div style={styles.contentWrap}>
            {/* Length helper banner if typing 1 or 2 characters */}
            {query.length > 0 && query.trim().length < 3 && (
              <div style={styles.hintStrip}>
                <span>Type at least 3 characters to search across products and materials</span>
              </div>
            )}

            {/* ========================================================= */}
            {/* 1. IDLE / FIRST VISIT STATE (No active 3+ char search)   */}
            {/* ========================================================= */}
            {!isSearching && (
              <div style={styles.idleStack}>
                {/* A. Recent Searches & History (If Any Exists) */}
                {(recentSearches.length > 0 || recentlyOpened.length > 0) && (
                  <div style={styles.sectionCard}>
                    <div style={styles.sectionHeaderRow}>
                      <h3 style={styles.sectionTitle}>Recent Searches</h3>
                      <button type="button" style={styles.clearBtn} onClick={clearHistory}>
                        Clear All
                      </button>
                    </div>

                    {/* Recently Viewed Carousel */}
                    {recentlyOpened.length > 0 && (
                      <div style={styles.recentlyOpenedGrid}>
                        {recentlyOpened.map((item, idx) => {
                          const id = item.id || item._id || idx;
                          const hasBrokenImg = brokenImages[`opened-${id}`];
                          const imgSrc = item.image
                            ? item.image.startsWith('http')
                              ? item.image
                              : `${IMAGE_URL}${item.image}`
                            : null;

                          return (
                            <div
                              key={id}
                              style={styles.openedCard}
                              onClick={() => handleItemPress(item)}
                            >
                              <div style={styles.openedImgBox}>
                                {imgSrc && !hasBrokenImg ? (
                                  <img
                                    src={imgSrc}
                                    alt={item.name}
                                    style={styles.openedImg}
                                    onError={() =>
                                      setBrokenImages((prev) => ({
                                        ...prev,
                                        [`opened-${id}`]: true,
                                      }))
                                    }
                                  />
                                ) : (
                                  <img
                                    src={ToteIcon}
                                    alt="fallback"
                                    style={{ width: 22, height: 22, opacity: 0.35 }}
                                  />
                                )}
                              </div>
                              <span style={styles.openedName} title={item.name}>
                                {item.name}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Recent Search Queries List */}
                    {recentSearches.length > 0 && (
                      <div style={styles.recentQueriesList}>
                        {recentSearches.map((term, index) => (
                          <div
                            key={index}
                            style={styles.recentQueryRow}
                            onClick={() => setQuery(term)}
                          >
                            <div style={styles.recentQueryLeft}>
                              <img
                                src={Clock}
                                alt="history"
                                style={{ width: 14, height: 14, opacity: 0.5 }}
                              />
                              <span style={styles.recentQueryText}>{term}</span>
                            </div>
                            <img
                              src={ArrowRight}
                              alt="go"
                              style={{ width: 12, height: 12, opacity: 0.4 }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* B. Trending Procurement Keywords (Zepto-style Pill Strip) */}
                <div style={styles.sectionCard}>
                  <div style={styles.trendingHeader}>
                    <img
                      src={ShootingStar}
                      alt="trending"
                      style={{
                        width: 16,
                        height: 16,
                        filter:
                          'invert(16%) sepia(85%) saturate(6120%) hue-rotate(348deg) brightness(91%) contrast(106%)',
                      }}
                    />
                    <h3 style={styles.sectionTitle}>Trending Searches</h3>
                  </div>

                  <div style={styles.pillsWrap}>
                    {TRENDING_SEARCHES.map((keyword, i) => (
                      <button
                        key={i}
                        type="button"
                        style={styles.pillChip}
                        onClick={() => setQuery(keyword)}
                      >
                        <span>{keyword}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* C. Quick Category Shortcuts */}
                <div style={styles.sectionCard}>
                  <h3 style={styles.sectionTitle}>Browse by Category</h3>
                  <div style={styles.categoryShortcutsGrid}>
                    {QUICK_CATEGORIES.map((cat, i) => (
                      <div
                        key={i}
                        style={styles.categoryShortcutCard}
                        onClick={() => setQuery(cat.query)}
                      >
                        <div style={styles.catIconWrap}>
                          <img
                            src={ToteIcon}
                            alt="cat"
                            style={{ width: 16, height: 16, opacity: 0.5 }}
                          />
                        </div>
                        <span style={styles.categoryShortcutText}>{cat.name}</span>
                        <img
                          src={ArrowRight}
                          alt="arrow"
                          style={{ width: 12, height: 12, opacity: 0.4, marginLeft: 'auto' }}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* D. Most Searched Products (Catalog Grid) */}
                {popularProducts.length > 0 && (
                  <div style={styles.sectionCard}>
                    <div style={styles.popularHeaderRow}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <img src={Lightning} alt="lightning" style={{ width: 15, height: 15 }} />
                        <h3 style={styles.sectionTitle}>Most Searched Products</h3>
                      </div>
                      <span style={styles.sectionSubtext}>High-demand site supplies</span>
                    </div>

                    <div style={styles.popularGrid}>
                      {popularProducts.map((p, idx) => {
                        const pid = p.product_id || p._id || idx;
                        const pName = p.product_name || p.name || 'Building Material';
                        const price = p.selling_price || p.buy_now?.unit_price || p.unit_price;
                        const pImg = p.product_image_url || p.product_image || p.image;
                        const fullImg = pImg
                          ? pImg.startsWith('http')
                            ? pImg
                            : `${IMAGE_URL}${pImg}`
                          : null;
                        const isBroken = brokenImages[`pop-${pid}`];

                        return (
                          <div
                            key={pid}
                            style={styles.popularCard}
                            onClick={() =>
                              navigate('/consumer/productdetails', { state: { productId: pid } })
                            }
                          >
                            <div style={styles.popularImgWrap}>
                              {fullImg && !isBroken ? (
                                <img
                                  src={fullImg}
                                  alt={pName}
                                  style={styles.popularImg}
                                  onError={() =>
                                    setBrokenImages((prev) => ({
                                      ...prev,
                                      [`pop-${pid}`]: true,
                                    }))
                                  }
                                />
                              ) : (
                                <img
                                  src={ToteIcon}
                                  alt="fallback"
                                  style={{ width: 24, height: 24, opacity: 0.35 }}
                                />
                              )}
                            </div>
                            <span style={styles.popularName} title={pName}>
                              {pName}
                            </span>
                            {price && (
                              <strong style={styles.popularPrice}>
                                ₹{Number(price).toLocaleString('en-IN')}
                              </strong>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ========================================================= */}
            {/* 2. LOADING STATE                                          */}
            {/* ========================================================= */}
            {(isLoading || isFetching) && (
              <div style={styles.loadingBox}>
                <span>Searching catalog items...</span>
              </div>
            )}

            {/* ========================================================= */}
            {/* 3. ACTIVE SEARCH RESULTS GRID                             */}
            {/* ========================================================= */}
            {!isLoading && isSearching && results.length > 0 && (
              <div style={styles.resultsWrapper}>
                <div style={styles.resultsHeader}>
                  <h3 style={styles.resultsTitle}>
                    Results for "{debouncedQuery}"
                  </h3>
                  <span style={styles.resultsCountBadge}>
                    {results.length} item{results.length !== 1 ? 's' : ''} found
                  </span>
                </div>

                <div style={styles.resultsGrid}>
                  {results.map((item, index) => {
                    const id = item.id || item._id || index;
                    const entityType = (item.type || 'product').toLowerCase();
                    const hasBroken = brokenImages[`res-${id}`];
                    const imgSrc = item.image || item.product_image_url || item.item_image;
                    const fullImg = imgSrc
                      ? imgSrc.startsWith('http')
                        ? imgSrc
                        : `${IMAGE_URL}${imgSrc}`
                      : null;

                    return (
                      <div
                        key={id}
                        style={styles.resultCard}
                        onClick={() => handleItemPress(item)}
                      >
                        <div style={styles.resultImgFrame}>
                          {fullImg && !hasBroken ? (
                            <img
                              src={fullImg}
                              alt={item.name}
                              style={styles.resultImg}
                              onError={() =>
                                setBrokenImages((prev) => ({
                                  ...prev,
                                  [`res-${id}`]: true,
                                }))
                              }
                            />
                          ) : (
                            <div style={styles.fallbackBox}>
                              <img
                                src={ToteIcon}
                                alt="fallback"
                                style={styles.toteFallback}
                              />
                            </div>
                          )}
                        </div>

                        <div style={styles.resultCardBody}>
                          <span
                            style={{
                              ...styles.entityBadge,
                              color:
                                entityType === 'category'
                                  ? '#1D4ED8'
                                  : entityType === 'subcategory'
                                  ? '#047857'
                                  : '#E9001E',
                              backgroundColor:
                                entityType === 'category'
                                  ? '#EFF6FF'
                                  : entityType === 'subcategory'
                                  ? '#ECFDF5'
                                  : '#FFF1F2',
                            }}
                          >
                            {entityType}
                          </span>

                          <h4 style={styles.resultItemName} title={item.name}>
                            {item.name}
                          </h4>

                          {item.selling_price && (
                            <span style={styles.resultPriceText}>
                              ₹{Number(item.selling_price).toLocaleString('en-IN')}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ========================================================= */}
            {/* 4. EMPTY SEARCH RESULTS                                   */}
            {/* ========================================================= */}
            {!isLoading && isSearching && results.length === 0 && (
              <div style={styles.emptyResultsBox}>
                <div style={styles.emptyIconCircle}>
                  <img
                    src={ToteIcon}
                    alt="no results"
                    style={{ width: 28, height: 28, opacity: 0.35 }}
                  />
                </div>
                <h4 style={styles.emptyTitle}>No results found for "{debouncedQuery}"</h4>
                <p style={styles.emptySubtitle}>
                  Check your spelling or explore categories directly from the home page.
                </p>
                <button
                  type="button"
                  style={styles.exploreCatalogBtn}
                  onClick={() => navigate('/consumer/HomeScreen')}
                >
                  Explore Catalog
                </button>
              </div>
            )}
          </div>
        </CenteredContainer>
      </main>
    </div>
  );
}

const styles = {
  pageShell: {
    backgroundColor: '#FAF9F6',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  mainContainer: {
    width: '100%',
    padding: '24px 0 60px 0',
  },
  contentWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    maxWidth: 900,
    margin: '0 auto',
    width: '100%',
  },
  hintStrip: {
    backgroundColor: '#F8FAFC',
    border: '1px solid #E2E8F0',
    borderRadius: 8,
    padding: '10px 16px',
    fontSize: 12.5,
    color: '#707B8C',
    textAlign: 'center',
  },
  idleStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  sectionCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    border: '1px solid #E2E8F0',
    padding: '18px 22px',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
  },
  sectionHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: 8,
    borderBottom: '1px solid #F1F5F9',
  },
  sectionTitle: {
    margin: 0,
    fontSize: 14.5,
    fontWeight: '700',
    color: '#2B2A29',
  },
  clearBtn: {
    background: 'none',
    border: 'none',
    color: '#E9001E',
    fontSize: 12,
    fontWeight: '700',
    cursor: 'pointer',
    padding: 0,
  },
  recentlyOpenedGrid: {
    display: 'flex',
    gap: 14,
    overflowX: 'auto',
    paddingBottom: 4,
  },
  openedCard: {
    width: 80,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    cursor: 'pointer',
    flexShrink: 0,
  },
  openedImgBox: {
    width: 60,
    height: 60,
    borderRadius: 10,
    backgroundColor: '#F8FAFC',
    border: '1px solid #E2E8F0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  openedImg: {
    maxWidth: '85%',
    maxHeight: '85%',
    objectFit: 'contain',
  },
  openedName: {
    fontSize: 11,
    color: '#475569',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '100%',
  },
  recentQueriesList: {
    display: 'flex',
    flexDirection: 'column',
  },
  recentQueryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: '1px solid #F1F5F9',
    cursor: 'pointer',
  },
  recentQueryLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  recentQueryText: {
    fontSize: 13.5,
    color: '#2B2A29',
    fontWeight: '500',
  },
  trendingHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  pillsWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  pillChip: {
    backgroundColor: '#FAF9F6',
    border: '1px solid #CBD5E1',
    borderRadius: 20,
    padding: '7px 14px',
    fontSize: 12.5,
    fontWeight: '600',
    color: '#2B2A29',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  categoryShortcutsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 10,
  },
  categoryShortcutCard: {
    border: '1px solid #E2E8F0',
    borderRadius: 8,
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    cursor: 'pointer',
    backgroundColor: '#FFFFFF',
    transition: 'border-color 0.15s ease',
  },
  catIconWrap: {
    width: 28,
    height: 28,
    borderRadius: 6,
    backgroundColor: '#F8FAFC',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  categoryShortcutText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#2B2A29',
  },
  popularHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  },
  sectionSubtext: {
    fontSize: 12,
    color: '#707B8C',
  },
  popularGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 12,
  },
  popularCard: {
    border: '1px solid #E2E8F0',
    borderRadius: 10,
    padding: 10,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    cursor: 'pointer',
    backgroundColor: '#FFFFFF',
  },
  popularImgWrap: {
    height: 90,
    backgroundColor: '#F8FAFC',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  popularImg: {
    maxWidth: '85%',
    maxHeight: '85%',
    objectFit: 'contain',
  },
  popularName: {
    fontSize: 12,
    fontWeight: '600',
    color: '#2B2A29',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  popularPrice: {
    fontSize: 13,
    color: '#E9001E',
    fontWeight: '700',
  },
  loadingBox: {
    padding: 60,
    textAlign: 'center',
    color: '#707B8C',
    fontSize: 14,
  },
  resultsWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  resultsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  },
  resultsTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: '800',
    color: '#2B2A29',
  },
  resultsCountBadge: {
    fontSize: 12.5,
    color: '#707B8C',
    fontWeight: '600',
  },
  resultsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: 16,
  },
  resultCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    border: '1px solid #CBD5E1',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    cursor: 'pointer',
    boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
  },
  resultImgFrame: {
    height: 140,
    backgroundColor: '#F8FAFC',
    borderBottom: '1px solid #F1F5F9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 10,
  },
  resultImg: {
    maxWidth: '90%',
    maxHeight: '90%',
    objectFit: 'contain',
  },
  fallbackBox: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  toteFallback: {
    width: 32,
    height: 32,
    opacity: 0.35,
  },
  resultCardBody: {
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  entityBadge: {
    fontSize: 9.5,
    fontWeight: '800',
    padding: '2px 6px',
    borderRadius: 4,
    textTransform: 'uppercase',
    alignSelf: 'flex-start',
    letterSpacing: '0.4px',
  },
  resultItemName: {
    margin: '2px 0 0 0',
    fontSize: 13,
    fontWeight: '700',
    color: '#2B2A29',
    lineHeight: '17px',
    height: 34,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  resultPriceText: {
    fontSize: 14,
    fontWeight: '800',
    color: '#2B2A29',
    marginTop: 2,
  },
  emptyResultsBox: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    border: '1px dashed #CBD5E1',
    padding: '48px 24px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  emptyIconCircle: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    backgroundColor: '#FFF1F2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
  },
  emptyTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: '700',
    color: '#2B2A29',
  },
  emptySubtitle: {
    margin: '6px 0 16px 0',
    fontSize: 12.5,
    color: '#707B8C',
    maxWidth: 320,
    lineHeight: '18px',
  },
  exploreCatalogBtn: {
    backgroundColor: '#E9001E',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 8,
    padding: '10px 20px',
    fontSize: 13,
    fontWeight: '700',
    cursor: 'pointer',
  },
};